import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from 'ethcss'
import translate from 'core/translate'
import styles from './templateEditor__contextMenu.jcss'

export type ContextMenuAction = {
    id: string
    label: string
    onClick: () => void
    disabled?: boolean
    danger?: boolean
}

type TemplateEditorContextMenuProps = {
    x: number
    y: number
    groups: ContextMenuAction[][]
    onClose: () => void
}

const VIEWPORT_PAD = 8

const fitMenuInViewport = (x: number, y: number, width: number, height: number) => {
    const left = Math.max(VIEWPORT_PAD, Math.min(x, window.innerWidth - width - VIEWPORT_PAD))
    const overflowsBottom = y + height > window.innerHeight - VIEWPORT_PAD
    const top = Math.max(VIEWPORT_PAD, overflowsBottom ? y - height : y)
    return { left, top }
}

export const TemplateEditorContextMenu: FC<TemplateEditorContextMenuProps> = ({ x, y, groups, onClose }) => {
    const menuRef = useRef<HTMLDivElement>(null)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose
    const [pos, setPos] = useState({ left: x, top: y })

    const sections = groups.filter((group) => group.length > 0)

    useLayoutEffect(() => {
        const menu = menuRef.current
        if (!menu) {
            return
        }
        const { width, height } = menu.getBoundingClientRect()
        setPos(fitMenuInViewport(x, y, width, height))
    }, [x, y, sections.length])

    useEffect(() => {
        const close = () => onCloseRef.current()

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close()
            }
        }

        const onPointer = (event: MouseEvent) => {
            if (menuRef.current?.contains(event.target as Node)) {
                return
            }
            close()
        }

        window.addEventListener('keydown', onKey)
        window.addEventListener('mousedown', onPointer, true)
        window.addEventListener('contextmenu', onPointer, true)

        return () => {
            window.removeEventListener('keydown', onKey)
            window.removeEventListener('mousedown', onPointer, true)
            window.removeEventListener('contextmenu', onPointer, true)
        }
    }, [])

    return createPortal(
        <div
            ref={menuRef}
            className={styles.menu}
            style={{ left: pos.left, top: pos.top }}
            role="menu"
            onMouseDown={(event) => event.stopPropagation()}
        >
            {sections.map((group, groupIndex) => (
                <div key={`group-${groupIndex}`}>
                    {groupIndex > 0 && <div className={styles.separator} role="separator" />}
                    {group.map((action) => (
                        <button
                            key={action.id}
                            type="button"
                            className={cn(styles.item, action.danger && styles.itemDanger)}
                            disabled={action.disabled}
                            role="menuitem"
                            onClick={() => {
                                if (!action.disabled) {
                                    action.onClick()
                                }
                                onClose()
                            }}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            ))}
            {sections.length === 0 && <div className={styles.empty}>{translate('empty')}</div>}
        </div>,
        document.body
    )
}
